import { Fragment, MouseEventHandler, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDrugInteractions, getPrescription } from '../../store/selectors/prescriptionPad';
import { getUnit } from '../../store/utils';
import { Button } from 'antd';
import MedicalCertificateDialog from './MedicalCertificateDialog';
import { Patient } from '../../store/types/patient';
import { getPatientByLocalId } from '../../store/idbHandler';
import { track } from '../../tracking';

const EXPAND_ICON = 'EXPAND_ICON';

const ConsentFormSuggestion: React.FC = () => {
    const [isExpanded, setExpanded] = useState(false);
    const [isMedicalDocumentDialogVisible, setMedicalDocumentDialogVisible] = useState(false);
    const [patient, setPatient] = useState<Patient | undefined>();

    const rx = useSelector(getPrescription);

    useEffect(() => {
        if (rx?.patient.localId) {
            getPatientByLocalId(rx.patient.localId).then((p) => setPatient(p));
        }
    }, [rx?.patient.localId]);

    const toggle = (): void => {
        document.getElementById(EXPAND_ICON)?.classList.toggle('rotate-180');
        setExpanded(!isExpanded);
        track(`consent-form-suggestion-expand`, { isExpanded: isExpanded.toString() });
    };

    const onSendNow: MouseEventHandler<HTMLElement> = (e): void => {
        e.stopPropagation();
        setMedicalDocumentDialogVisible(true);
        track('consent-form-suggestion-send-now');
    };

    return (
        <Fragment>
            {isMedicalDocumentDialogVisible && patient && rx && (
                <MedicalCertificateDialog
                    closeMe={() => setMedicalDocumentDialogVisible(false)}
                    patient={patient}
                    defaultState={{
                        selectedCertificate: 'hivc',
                        type: 'consent',
                        page: 'create-cr',
                    }}
                    docid={rx?.doctor._id}
                />
            )}
            <div className="flex flex-col items-center  px-12 py-10 mx-4 border cursor-pointer border-status-warning bg-status-warningBG shadow-tight-1 rounded-4 space-y-10">
                <div
                    onClick={toggle}
                    className="flex items-center justify-between w-full select-none"
                >
                    <div className="flex items-center space-x-12">
                        <div className="flex items-center justify-center w-16 h-16">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 576 512"
                                className="fill-status-warning"
                                width="16"
                                height="16"
                            >
                                <path d="M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0l239.94 416.028zM288 354c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                            </svg>
                        </div>
                        <p className="text-12 text-status-warning">HIV Test Consent Required</p>
                    </div>
                    <div className="flex space-x-16 justify-center items-center">
                        <Button onClick={onSendNow}>Send now</Button>
                        <div
                            id={EXPAND_ICON}
                            className="transform-gpu transition-all duration-200 ease-in-out flex items-center justify-center w-16 h-16"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                width="12"
                                height="12"
                                className="cursor-pointer fill-status-warning"
                            >
                                <path d="M441.9 167.3l-19.8-19.8c-4.7-4.7-12.3-4.7-17 0L224 328.2 42.9 147.5c-4.7-4.7-12.3-4.7-17 0L6.1 167.3c-4.7 4.7-4.7 12.3 0 17l209.4 209.4c4.7 4.7 12.3 4.7 17 0l209.4-209.4c4.7-4.7 4.7-12.3 0-17z" />
                            </svg>
                        </div>
                    </div>
                </div>
                {isExpanded && (
                    <div className="opacity-anim hover:bg-accent-6-15 flex items-center justify-between w-full p-8 mx-8 bg-accent-3-23 rounded-4 contraceptions">
                        Before prescribing an HIV test, it is essential to ensure that the patient
                        provides informed consent. Kindly send the consent form to the patient for
                        review and signature. Patient consent is crucial for conducting HIV tests,
                        and it is a legal and ethical requirement. Ensure that the patient
                        understands the nature of the test, its purpose, and the potential
                        implications of the results. If you have any questions or concerns regarding
                        the consent process, please consult your institution's policies or legal
                        advisors. Thank you for your attention to this important matter.
                    </div>
                )}
            </div>
        </Fragment>
    );
};

export default ConsentFormSuggestion;
